import { BlockTypeEnum, clientPageTypeEnum, LIST_PAGE_TYPES, PREVIEW_PAGE_TYPES } from '../../../core/enums';

/** Zapamiętana strona wejścia do podglądu — żyje w sessionStorage do zamknięcia karty. */
export const BACK_TO_LIST_ENTRY_STORAGE_KEY = 'backToListEntry';

/**
 * Znacznik jednorazowy: kolejne wczytanie strony jest przeładowaniem technicznym
 * (zmiana waluty), a nie nowym wejściem odwiedzającego ani odświeżeniem strony.
 */
export const BACK_TO_LIST_KEEP_ON_RELOAD_STORAGE_KEY = 'backToListKeepOnReload';

export interface BackToListEntry {
  sourceUrl: string;
  sourceLocale: string;
  previewUrl: string;
}

export interface BackToListTargetParams {
  currentUrl: string;
  currentLocale: string;
  storedEntry: BackToListEntry | null;
}

export interface BackToListKeepParams {
  storedEntry: BackToListEntry | null;
  targetUrl: string;
}

export interface SamePageNavigationParams {
  sourceUrl: string | null;
  targetUrl: string;
}

export interface ChildPreviewParams {
  sourceUrl: string;
  targetUrl: string;
}

export interface NavigationSourceParams {
  navigationTrigger: 'imperative' | 'popstate' | 'hashchange' | null;
  browserUrl: string;
  routerUrl: string;
}

/**
 * Kształt bloku w danych strony klienckiej. Blok osadzony przez redaktora zostaje
 * nośnikiem o własnym `type` (zwykle uniwersalnym), a backend dokłada mu typ
 * bloku specjalnego w `specialBlockType` i całą jego treść w `specialBlock`.
 */
export interface BlockLike {
  type: BlockTypeEnum;
  children?: BlockLike[];
  specialBlockId?: number | null;
  specialBlockType?: BlockTypeEnum | null;
  specialBlock?: BlockLike | null;
}

export interface PageLike {
  type: clientPageTypeEnum;
  locale?: string;
  blocks?: BlockLike[];
}

const EMBEDDED_LIST_BLOCK_TYPES = [
  BlockTypeEnum.INVENTORIES_LIST_TYPE_WWW,
  BlockTypeEnum.EXHIBITIONS_LIST_TYPE,
  BlockTypeEnum.NEWS_LIST_TYPE,
  BlockTypeEnum.ARTISTS_LIST_TYPE_WWW,
  BlockTypeEnum.GLOBAL_SEARCH_RESULTS_LIST_TYPE,
];

export const getBackToListTargetUrl = ({
  currentUrl,
  currentLocale,
  storedEntry,
}: BackToListTargetParams): string => {
  // Zapamiętana strona wejścia obowiązuje tylko na tym podglądzie i w tym języku:
  // po zmianie języka świadomie schodzimy do reguły 2. Porównujemy same ścieżki,
  // bo parametry adresu podglądu zmieniają się w trakcie wizyty (waluta, filtry
  // osadzonych list, lightbox) i nie oznaczają wejścia na inną stronę.
  if (
    storedEntry &&
    getUrlPath(storedEntry.previewUrl) === getUrlPath(currentUrl) &&
    storedEntry.sourceLocale === currentLocale
  ) {
    return storedEntry.sourceUrl;
  }

  return getParentUrl(currentUrl);
};

export const getParentUrl = (url: string): string => {
  const segments = getUrlPath(url).split('/').filter(Boolean);

  if (segments.length <= 1) {
    return '/';
  }

  return `/${segments.slice(0, -1).join('/')}`;
};

/**
 * Czy zapamiętana strona wejścia dotyczy podglądu wczytywanego pod podanym adresem.
 * Używane przy przeładowaniu strony, gdy nie znamy adresu strony opuszczanej.
 */
export const shouldKeepStoredEntry = ({storedEntry, targetUrl}: BackToListKeepParams): boolean => {
  return !!storedEntry && getUrlPath(storedEntry.previewUrl) === getUrlPath(targetUrl);
};

/**
 * Czy nawigacja pozostaje na tej samej stronie (zmieniają się tylko parametry adresu:
 * filtry osadzonej listy, lightbox, waluta). Takie przejście nie jest wejściem z innej
 * strony, więc nie może kasować pamięci powrotu — w odróżnieniu od powrotu na ten sam
 * podgląd skądinąd, który regułę 1 unieważnia.
 */
export const isNavigationWithinSamePage = ({sourceUrl, targetUrl}: SamePageNavigationParams): boolean => {
  return !!sourceUrl && getUrlPath(sourceUrl) === getUrlPath(targetUrl);
};

/**
 * Adres strony opuszczanej przez nawigację.
 *
 * Paginacja osadzonych list przestawia adres przez `location.go()`, z pominięciem
 * routera, więc `Router.url` nie zna numeru strony ani wybranego widoku — dla zwykłej
 * nawigacji bierzemy więc adres z paska. Przy wstecz/dalej jest odwrotnie: przeglądarka
 * przestawia pasek na adres docelowy jeszcze przed nawigacją, więc jedynym adresem
 * strony opuszczanej pozostaje ten z routera.
 */
export const resolveNavigationSourceUrl = (
  {navigationTrigger, browserUrl, routerUrl}: NavigationSourceParams,
): string => {
  return navigationTrigger === 'imperative' ? browserUrl : routerUrl;
};

export const pageHasEmbeddedListBlock = (blocks: BlockLike[] = []): boolean => {
  return blocks.some((block) => {
    if (isListBlock(block)) {
      return true;
    }

    // Lista może siedzieć w zwykłych dzieciach nośnika albo w treści innego bloku specjalnego.
    return pageHasEmbeddedListBlock(block.children || [])
      || pageHasEmbeddedListBlock(block.specialBlock?.children || []);
  });
};

export const canBeBackToListSource = (page: PageLike | null | undefined): boolean => {
  if (!page) {
    return false;
  }

  // Oferta jest technicznie stroną podglądową, ale z natury jest stroną z listą prac:
  // katalog jest całą jej treścią, a przycisk powrotu stoi tylko w podglądzie pracy tej
  // oferty. Reguła 1 i reguła 2 wskazują dla niej ten sam adres, więc dopuszczenie jej
  // jako źródła nie może przenieść odwiedzającego na inną stronę — dokłada wyłącznie
  // zapamiętane parametry adresu (filtry, sortowanie, numer strony). Bez tego wyjątku
  // reguła 1 byłaby w całym module ofert nieosiągalna.
  if (page.type === clientPageTypeEnum.PRIVATE_VIEW_TYPE) {
    return true;
  }

  // Wejście z podglądu innego obiektu podlega regule 2, nawet gdy ten podgląd
  // zawiera katalog prac albo listę aktualności.
  if (PREVIEW_PAGE_TYPES.includes(page.type)) {
    return false;
  }

  return LIST_PAGE_TYPES.includes(page.type) || pageHasEmbeddedListBlock(page.blocks);
};

/**
 * Czy podgląd, z którego wychodzimy, jest nośnikiem listy — czyli stroną podglądową
 * z osadzonym blokiem listy (katalog prac na wystawie, katalog na podglądzie artysty).
 * Sam ten warunek nie czyni go jeszcze źródłem powrotu: rozstrzyga dopiero relacja
 * adresów, którą sprawdza `isChildPreviewOfSource`.
 */
export const hostsEmbeddedListPreview = (page: PageLike | null | undefined): boolean => {
  return !!page && PREVIEW_PAGE_TYPES.includes(page.type) && pageHasEmbeddedListBlock(page.blocks);
};

/**
 * Czy cel nawigacji jest dzieckiem strony opuszczanej w adresie — tak jest dla pracy
 * wystawy (`/pl/wystawy/<wystawa>/<praca>`) i dla pracy oferty.
 *
 * Ta relacja jest warunkiem dopuszczenia podglądu jako źródła powrotu. Gdy zachodzi,
 * reguła 1 i reguła 2 wskazują tę samą stronę, więc zapamiętane wejście nie może
 * przenieść odwiedzającego gdzie indziej — dokłada wyłącznie parametry adresu (filtry,
 * sortowanie, numer strony osadzonej listy). Bez tego warunku podgląd wystawy przejmowałby
 * rolę listy także dla wyjścia na aktualność albo artystę, czyli dla obiektów, których
 * własna lista leży zupełnie gdzie indziej.
 */
export const isChildPreviewOfSource = ({sourceUrl, targetUrl}: ChildPreviewParams): boolean => {
  return getParentUrl(targetUrl) === getUrlPath(sourceUrl);
};

/**
 * Adres bez parametrów zapytania, kotwicy i końcowego ukośnika, zdekodowany procentowo —
 * sama ścieżka strony, znormalizowana niezależnie od tego, czy przyszła z routera
 * (koduje niełatynicę, np. „ł" jako „%C5%82") czy z paska przeglądarki. Popsute
 * kodowanie procentowe (samotny „%") nie może wywalić nawigacji, więc przy błędzie
 * `decodeURIComponent` zostaje ścieżka nieodkodowana.
 */
export const getUrlPath = (url: string): string => {
  const path = url.split('#')[0].split('?')[0];
  const trimmed = path.length > 1 ? path.replace(/\/+$/, '') : path;

  try {
    return decodeURIComponent(trimmed);
  } catch {
    return trimmed;
  }
};

const isListBlock = (block: BlockLike): boolean => {
  return [block.type, block.specialBlockType, block.specialBlock?.type]
    .some((type) => type !== null && type !== undefined && EMBEDDED_LIST_BLOCK_TYPES.includes(type));
};
